/**
 * ============================================================================
 * Quality 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Quality（质量管理）模块的所有 API 请求方法
 * - 包含：检验项目库、检验模板、检验任务、NCR、AQL、自检配置、看板
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * @module api/quality/factory
 * @description Quality（质量管理）模块的 API 工厂函数
 * @generated 2025-12-06
 * @author AI Assistant
 * ============================================================================
 */

// ============================================================================
// 类型导入
// ============================================================================

import type {
  InspectionItem,
  InspectionTemplate,
  InspectionTemplateItem,
  InspectionTask,
  InspectionRecord,
  NCROrder,
  DefectReason,
  SelfInspectionConfig,
  // 8D报告类型
  EightDReport,
  EightDReportListItem,
  EightDTeamMember,
  EightDNCRLink,
  EightDLog,
  EightDSLAConfig,
  EightDStatistics,
  EightDReportQueryParams,
  CreateEightDReportForm,
  UpdateEightDReportForm,
  UpdateEightDD0Form,
  UpdateEightDD1Form,
  UpdateEightDD2Form,
  UpdateEightDD3Form,
  UpdateEightDD4Form,
  UpdateEightDD5Form,
  UpdateEightDD6Form,
  UpdateEightDD7Form,
  UpdateEightDD8Form,
  AddEightDTeamMemberForm,
  AddEightDTeamMembersBatchForm,
  LinkEightDNCRForm,
  EightDStepBackwardForm,
  CloseEightDReportForm,
  CancelEightDReportForm,
  UpdateEightDSLAConfigForm,
  CreateEightDFromQRQCForm,
  // 检验汇总看板类型
  InspectionSummaryQueryParams,
  PaginatedQueryParams,
  DefectTopQueryParams,
  TrendQueryParams,
  DateRangeOptionsResponse,
  MetricExplanationsResponse,
  SummaryResponse,
  TaskTypeAnalysisResponse,
  ProductAnalysisResponse,
  MaterialAnalysisResponse,
  SupplierAnalysisResponse,
  InspectorAnalysisResponse,
  WorkstationAnalysisResponse,
  WorkshopAnalysisResponse,
  DefectTopResponse,
  TrendResponse,
  ProductDetailResponse,
  SupplierDetailResponse,
  NcrSummaryResponse,
} from '../../types/quality'

import type { PaginationResult } from '../../types/api'
import type { IRequestClient } from '../core/client'
import { API_ROUTES } from '../routes'

// ============================================================================
// 查询参数类型定义
// ============================================================================

/** 检验项目查询参数 */
export interface InspectionItemQueryParams {
  page?: number
  per_page?: number
  keyword?: string
  item_type?: string
  value_type?: string
  status?: number
}

/** 检验模板查询参数 */
export interface InspectionTemplateQueryParams {
  page?: number
  per_page?: number
  keyword?: string
  template_type?: string
  product_id?: number
  material_id?: number
  operation_id?: number
  status?: number
}

/** 检验任务查询参数 */
export interface InspectionTaskQueryParams {
  page?: number
  per_page?: number
  keyword?: string
  task_type?: string
  status?: string
  result?: string
  start_date?: string
  end_date?: string
  inspector_id?: number
  /** 工序ID筛选 */
  operation_id?: number
  /** 工单工序ID筛选 */
  work_order_operation_id?: number
  /** 产品ID筛选 */
  product_id?: number
  /** 物料ID筛选 */
  material_id?: number
}

/** NCR 订单查询参数 */
export interface NCROrderQueryParams {
  page?: number
  per_page?: number
  keyword?: string
  status?: string
  source_type?: string
  defect_level?: string
  start_date?: string
  end_date?: string
  /** 责任归属类型：SUPPLIER/INTERNAL/CUSTOMER */
  responsibility_type?: string
  /** 是否为 QRQC：1=是，0=否 */
  is_qrqc?: number
}

/** AQL 标准查询参数 */
export interface AQLStandardQueryParams {
  page?: number
  per_page?: number
  level?: string
  status?: number
}

/** 自检配置查询参数 */
export interface SelfInspectionConfigQueryParams {
  page?: number
  per_page?: number
  product_id?: number
  workstation_id?: number
  status?: number
}

// ============================================================================
// 表单类型定义
// ============================================================================

/** 创建检验项目表单 */
export interface CreateInspectionItemForm {
  item_code: string
  item_name: string
  item_type: string
  value_type: string
  unit?: string
  options?: string[]
  qualified_options?: string[]
  default_standard_value?: string
  default_upper_limit?: number
  default_lower_limit?: number
  gauge_id?: number
  inspection_method?: string
  reference_document?: string
  status?: number
  sort_order?: number
}

/** 更新检验项目表单 */
export type UpdateInspectionItemForm = Partial<CreateInspectionItemForm>

/** 创建检验模板表单 */
export interface CreateInspectionTemplateForm {
  template_code: string
  template_name: string
  template_type: string
  product_id?: number
  material_id?: number
  operation_id?: number
  customer_id?: number
  supplier_id?: number
  sampling_type?: string
  aql_standard_id?: number
  aql_level?: string
  aql_value?: number
  fixed_sample_size?: number
  version?: string
  effective_date?: string
  expiry_date?: string
  description?: string
  status?: number
}

/** 更新检验模板表单 */
export type UpdateInspectionTemplateForm = Partial<CreateInspectionTemplateForm>

/** 添加模板检验项表单 */
export interface AddTemplateItemForm {
  item_id: number
  standard_value?: string
  upper_limit?: number
  lower_limit?: number
  qualified_options?: string[]
  is_critical?: boolean
  is_required?: boolean
  gauge_id?: number
  sort_order?: number
  remark?: string
}

/** 更新模板检验项表单 */
export type UpdateTemplateItemForm = Partial<AddTemplateItemForm>

/** 创建检验任务表单 */
export interface CreateInspectionTaskForm {
  task_type: string
  template_id?: number
  batch_no?: string
  source_type?: string
  source_id?: number
  source_no?: string
  product_id?: number
  material_id?: number
  work_order_id?: number
  operation_id?: number
  workstation_id?: number
  lot_quantity?: number
  sample_quantity?: number
  assigned_to?: number
  plan_date?: string
  remark?: string
}

/** 录入检验记录表单 */
export interface RecordInspectionForm {
  /** 检验记录 id：后端按 task_id + id 匹配更新，不存在的 id 会被静默跳过 */
  id: number
  template_item_id: number
  actual_value: string
  actual_numeric?: number
  gauge_id?: number
  attachments?: string[]
  remark?: string
}

/** 完成检验任务表单 */
export interface CompleteTaskForm {
  result_remark?: string
  attachments?: string[]
  /** 是否创建NCR（当有非关键项不合格时由检验员决定） */
  create_ncr?: boolean
  /** 不良原因ID（create_ncr 时采集，来自不良原因库；现场新录入的原因先经快速创建接口入库） */
  defect_reason_id?: number
  /** 责任人账号ID（create_ncr 时采集，有值则 NCR 直达「待确认」） */
  responsible_person_id?: number
  /** 责任人员工ID（responsible_person_id 未传时后端按员工反查账号） */
  responsible_employee_id?: number
}

/** 完成检验任务返回结果 */
export interface CompleteTaskResult {
  taskId: number
  taskNo: string
  status: string
  statusName: string
  result: string
  resultName: string
  qualifiedItems: number
  rejectedItems: number
  completedAt: string | null
  ncrOrder?: {
    id: number
    ncrNo: string
  } | null
}

/** 让步接收表单 */
export interface ConcessionForm {
  concession_remark: string
  approval_id?: number
}

/** 新增检验样本数量表单 */
export interface AddSamplesForm {
  /** 新增的样本数量 */
  additional_quantity: number
}

/** 创建 NCR 表单 */
export interface CreateNCROrderForm {
  source_type: string
  inspection_task_id?: number
  product_id?: number
  material_id?: number
  batch_no?: string
  work_order_no?: string
  work_order_id?: number
  defect_quantity: number
  defect_description: string
  defect_level: string
  defect_category?: string
  /** 不良原因ID（原因库启用项；创建时快照名称并自增原因用量；编辑传 null 表示清空） */
  defect_reason_id?: number | null
  responsibility_type?: string
  responsible_dept_id?: number
  responsible_person_id?: number
  /** 责任人员工ID（mes_employees.id，员工选择器语义，后端反查系统账号） */
  responsible_employee_id?: number
  remark?: string
  /** 缺陷图片URL列表 */
  defect_photos?: string[]
  /** 是否直接提交评审（true=待评审，false=草稿） */
  submit_for_review?: boolean
}

/** NCR 处置表单 */
export interface NCRDispositionForm {
  disposition: string
  disposition_detail?: string
}

/** 不良原因列表查询参数 */
export interface DefectReasonQueryParams {
  keyword?: string
  category?: string
  /** 状态：1启用 0停用；不传查全部 */
  status?: number | ''
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  page?: number
  per_page?: number
}

/** 创建不良原因表单 */
export interface DefectReasonStoreForm {
  /** 原因名称（唯一，≤50字） */
  name: string
  /** 分组 */
  category?: string
  /** 说明 */
  description?: string
  /** 排序值 */
  sort_order?: number
  /** 状态：1启用 0停用 */
  status?: number
}

/** 更新不良原因表单 */
export interface DefectReasonUpdateForm {
  name?: string
  category?: string | null
  description?: string | null
  sort_order?: number
  status?: number
}

/** 现场快速创建不良原因表单（移动端） */
export interface DefectReasonQuickCreateForm {
  name: string
  category?: string
}

/** 批量创建不良原因表单 */
export interface DefectReasonBatchCreateForm {
  /** 原因名称列表（≤100 条，逐条 ≤50 字；同名启用跳过，软删/停用恢复） */
  names: string[]
  /** 统一分组（可选） */
  category?: string
}

/** 批量创建结果 */
export interface DefectReasonBatchCreateResult {
  /** 新建的原因名 */
  created: string[]
  /** 恢复（原停用/软删）的原因名 */
  restored: string[]
  /** 跳过（同名启用已存在）的原因名 */
  skipped: string[]
}

/** NCR 执行表单 */
export interface NCRExecuteForm {
  execution_detail: string
  attachments?: string[]
}

/** QRQC 升级表单 */
export interface EscalateToQRQCForm {
  /** QRQC 级别：LINE/SHOP/PLANT */
  qrqc_level: string
  /** Why 1 */
  why1: string
  /** Why 2 */
  why2?: string
  /** Why 3 */
  why3?: string
  /** Why 4 */
  why4?: string
  /** Why 5 */
  why5?: string
  /** 根因总结 */
  root_cause: string
  /** 纠正措施 */
  corrective_action: string
  /** 纠正措施责任人ID */
  corrective_action_owner_id?: number
  /** 纠正措施截止日期 */
  corrective_action_due_date?: string
  /** 预防措施 */
  preventive_action?: string
  /** 预防措施责任人ID */
  preventive_action_owner_id?: number
  /** 预防措施截止日期 */
  preventive_action_due_date?: string
}

/** 创建 AQL 标准表单 */
export interface CreateAQLStandardForm {
  level: string
  lot_size_min: number
  lot_size_max: number
  sample_code: string
  aql_values: Record<string, { accept: number; reject: number }>
  status?: number
}

/** 批量导入检验项目表单 */
export interface BatchImportInspectionItemForm {
  item_code: string
  item_name: string
  item_type?: string
  value_type?: string
  unit?: string
  options?: string[] | string
  qualified_options?: string[] | string
  default_standard_value?: string
  default_upper_limit?: number
  default_lower_limit?: number
  gauge_code?: string
  gauge_name?: string
  inspection_method?: string
  reference_document?: string
  sort_order?: number
}

/** 批量导入检验项目结果 */
export interface BatchImportInspectionItemsResult {
  success_count: number
  failed_count: number
  total: number
  created_items: { id: number; item_code: string; item_name: string }[]
  updated_items: { id: number; item_code: string; item_name: string }[]
  errors: { row: number; item_code: string; error: string }[]
}

/** 导入模板字段信息 */
export interface ImportTemplateField {
  field: string
  label: string
  required: boolean
  type: string
  max_length?: number
  default?: string | number
  options?: Record<string, string>
  /** 字段允许字符说明，用于导入模板下载页提示 */
  allowed_chars?: string
  /** 字段禁止字符说明，用于前端解析导入文件时给出友好提示 */
  forbidden_chars?: string[]
  /** 后端字段校验正则，供导入模板说明展示 */
  pattern?: string
  description: string
  example: string | number
}

/** 导入模板信息 */
export interface ImportTemplateInfo {
  fields: ImportTemplateField[]
  examples: Record<string, any>[]
  instructions: string[]
  item_fields?: ImportTemplateField[]
}

/** 批量导入检验模板表单 */
export interface BatchImportTemplateForm {
  template_code: string
  template_name: string
  template_type?: string
  product_code?: string
  material_code?: string
  operation_code?: string
  sampling_type?: string
  aql_level?: string
  aql_value?: number
  fixed_sample_size?: number
  description?: string
  version?: string
  items?: BatchImportTemplateItemForm[]
}

/** 批量导入模板检验项表单 */
export interface BatchImportTemplateItemForm {
  item_code: string
  standard_value?: string
  upper_limit?: number
  lower_limit?: number
  is_critical?: boolean
  is_required?: boolean
  sort_order?: number
}

/** 批量导入检验模板结果 */
export interface BatchImportTemplatesResult {
  success_count: number
  failed_count: number
  total: number
  created_templates: { id: number; template_code: string; template_name: string }[]
  updated_templates: { id: number; template_code: string; template_name: string }[]
  errors: { row: number; template_code: string; error: string }[]
}

/** 创建自检配置表单 */
export interface CreateSelfInspectionConfigForm {
  /** 产品ID（可选，留空=该工位的通用自检规则） */
  product_id?: number
  /** 工位ID（必填） */
  workstation_id: number
  /** 检验模板ID（必填） */
  template_id: number
  /** 频次类型：TIME-按时间 QUANTITY-按数量 BATCH-按批次 */
  frequency_type: string
  /** 频次值 */
  frequency_value: number
  /** 时间单位（频次类型为TIME时使用）：MINUTE-分钟 HOUR-小时 */
  frequency_unit?: string
  /** 是否启用提醒 */
  enable_reminder?: boolean
  /** 提前提醒分钟数 */
  reminder_advance_minutes?: number
  /** 状态：1-启用 0-禁用 */
  status?: number
  /** 备注 */
  remark?: string
}

// ============================================================================
// Quality API 工厂函数
// ============================================================================

/**
 * 创建 Quality 模块的 API 方法集合
 */
export function createQualityApi(client: IRequestClient) {
  return {
    // ============================================================================
    // 检验项目库 API
    // ============================================================================
    
    /**
     * 获取检验项目列表（分页）
     */
    getInspectionItemList(params?: InspectionItemQueryParams) {
      return client.get<PaginationResult<InspectionItem>>(
        API_ROUTES.quality.list,
        { params }
      )
    },
    
    /**
     * 获取所有检验项目（不分页）
     */
    getAllInspectionItems() {
      return client.get<InspectionItem[]>(API_ROUTES.quality.all)
    },
    
    /**
     * 获取检验项目详情
     */
    getInspectionItemDetail(id: number) {
      return client.get<InspectionItem>(API_ROUTES.quality.detail(id))
    },
    
    /**
     * 创建检验项目
     */
    createInspectionItem(data: CreateInspectionItemForm) {
      return client.post<InspectionItem>(API_ROUTES.quality.inspectionItems, data)
    },
    
    /**
     * 更新检验项目
     */
    updateInspectionItem(id: number, data: UpdateInspectionItemForm) {
      return client.put<InspectionItem>(API_ROUTES.quality.qualityInspectionItems(id), data)
    },
    
    /**
     * 删除检验项目
     */
    deleteInspectionItem(id: number) {
      return client.delete<void>(API_ROUTES.quality.delete(id))
    },
    
    /**
     * 更新检验项目状态
     */
    updateInspectionItemStatus(id: number, status: number) {
      return client.put<InspectionItem>(API_ROUTES.quality.update(id), { status })
    },
    
    /**
     * 导入检验项目（Excel文件方式，保留兼容）
     */
    importInspectionItems(file: FormData) {
      return client.post<{ success: number; failed: number; errors: string[] }>(
        API_ROUTES.quality.import,
        file
      )
    },
    
    /**
     * 批量导入检验项目（前端解析Excel，后端接收JSON）
     * 推荐使用此方法，支持详细的行级错误返回
     */
    batchImportInspectionItems(data: { items: BatchImportInspectionItemForm[] }) {
      return client.post<BatchImportInspectionItemsResult>(
        API_ROUTES.quality.batchImport,
        data
      )
    },
    
    /**
     * 获取检验项目导入模板和填写说明
     */
    getInspectionItemImportTemplate() {
      return client.get<ImportTemplateInfo>(API_ROUTES.quality.inspectionItemsImportTemplate)
    },
    
    /**
     * 导出检验项目
     */
    exportInspectionItems(params?: InspectionItemQueryParams) {
      return client.get<Blob>(API_ROUTES.quality.export, { 
        params,
        responseType: 'blob'
      })
    },
    
    /**
     * 获取值类型选项
     */
    getValueTypes() {
      return client.get<{ value: string; label: string }[]>(
        API_ROUTES.quality.inspectionItemsValueTypes
      )
    },
    
    // ============================================================================
    // 检验模板 API
    // ============================================================================
    
    /**
     * 获取检验模板列表（分页）
     */
    getTemplateList(params?: InspectionTemplateQueryParams) {
      return client.get<PaginationResult<InspectionTemplate>>(
        API_ROUTES.quality.templates,
        { params }
      )
    },
    
    /**
     * 获取检验模板详情
     */
    getTemplateDetail(id: number) {
      return client.get<InspectionTemplate>(API_ROUTES.quality.qualityTemplates(id))
    },
    
    /**
     * 创建检验模板
     */
    createTemplate(data: CreateInspectionTemplateForm) {
      return client.post<InspectionTemplate>(API_ROUTES.quality.templates, data)
    },
    
    /**
     * 更新检验模板
     */
    updateTemplate(id: number, data: UpdateInspectionTemplateForm) {
      return client.put<InspectionTemplate>(API_ROUTES.quality.qualityTemplates(id), data)
    },
    
    /**
     * 删除检验模板
     */
    deleteTemplate(id: number) {
      return client.delete<void>(API_ROUTES.quality.qualityTemplates(id))
    },
    
    /**
     * 复制检验模板
     */
    copyTemplate(id: number) {
      return client.post<InspectionTemplate>(API_ROUTES.quality.copy(id))
    },
    
    /**
     * 创建模板新版本
     */
    createTemplateNewVersion(id: number, data: { version: string; effective_date?: string }) {
      return client.post<InspectionTemplate>(API_ROUTES.quality.newVersion(id), data)
    },
    
    /**
     * 获取模板版本历史
     */
    getTemplateVersions(id: number) {
      return client.get<InspectionTemplate[]>(API_ROUTES.quality.templatesVersions(id))
    },
    
    /**
     * 添加模板检验项
     */
    addTemplateItem(templateId: number, data: AddTemplateItemForm) {
      return client.post<InspectionTemplateItem>(
        API_ROUTES.quality.addItem(templateId),
        data
      )
    },
    
    /**
     * 更新模板检验项
     */
    updateTemplateItem(templateId: number, itemId: number, data: UpdateTemplateItemForm) {
      return client.put<InspectionTemplateItem>(
        API_ROUTES.quality.items(templateId, itemId),
        data
      )
    },
    
    /**
     * 删除模板检验项
     */
    deleteTemplateItem(templateId: number, itemId: number) {
      return client.delete<void>(API_ROUTES.quality.templatesItems(templateId, itemId))
    },
    
    /**
     * 排序模板检验项
     */
    sortTemplateItems(templateId: number, itemIds: number[]) {
      return client.put<void>(API_ROUTES.quality.itemsSort(templateId), { ordered_ids: itemIds })
    },
    
    /**
     * 获取模板类型选项
     */
    getTemplateTypes() {
      return client.get<{ value: string; label: string }[]>(API_ROUTES.quality.templatesTypes)
    },
    
    /**
     * 获取抽样类型选项
     */
    getSamplingTypes() {
      return client.get<{ value: string; label: string }[]>(API_ROUTES.quality.templatesSamplingTypes)
    },
    
    /**
     * 匹配检验模板
     */
    matchTemplate(params: { template_type: string; product_id?: number; material_id?: number; operation_id?: number }) {
      return client.get<InspectionTemplate | null>(API_ROUTES.quality.templatesMatch, { params })
    },
    
    /**
     * 批量导入检验模板（前端解析Excel，后端接收JSON）
     */
    batchImportTemplates(data: { templates: BatchImportTemplateForm[] }) {
      return client.post<BatchImportTemplatesResult>(
        API_ROUTES.quality.templatesBatchImport,
        data
      )
    },
    
    /**
     * 获取检验模板导入模板和填写说明
     */
    getTemplateImportTemplate() {
      return client.get<ImportTemplateInfo>(API_ROUTES.quality.templatesImportTemplate)
    },
    
    // ============================================================================
    // 检验任务 API
    // ============================================================================
    
    /**
     * 获取检验任务列表（分页）
     */
    getTaskList(params?: InspectionTaskQueryParams) {
      return client.get<PaginationResult<InspectionTask>>(
        API_ROUTES.quality.tasks,
        { params }
      )
    },
    
    /**
     * 获取待检任务
     */
    getPendingTasks(params?: InspectionTaskQueryParams) {
      return client.get<PaginationResult<InspectionTask>>(
        API_ROUTES.quality.tasksPending,
        { params }
      )
    },
    
    /**
     * 获取我的任务
     */
    getMyTasks(params?: InspectionTaskQueryParams) {
      return client.get<PaginationResult<InspectionTask>>(
        API_ROUTES.quality.inspectionTasksMy,
        { params }
      )
    },
    
    /**
     * 获取任务统计
     */
    getTaskStatistics() {
      // 键与后端 InspectionTaskService::getStatistics（convertStatisticsKeys 后）一致
      return client.get<{
        total: number
        pending: number
        inProgress: number
        completed: number
        qualified: number
        rejected: number
        conditional: number
      }>(API_ROUTES.quality.statistics)
    },
    
    /**
     * 获取检验任务详情
     */
    getTaskDetail(id: number) {
      return client.get<InspectionTask>(API_ROUTES.quality.qualityTasks(id))
    },
    
    /**
     * 创建检验任务
     */
    createTask(data: CreateInspectionTaskForm) {
      return client.post<InspectionTask>(API_ROUTES.quality.tasks, data)
    },
    
    /**
     * 开始检验
     */
    startTask(id: number) {
      return client.post<InspectionTask>(API_ROUTES.quality.start(id))
    },
    
    /**
     * 录入检验记录
     */
    recordInspection(taskId: number, records: RecordInspectionForm[]) {
      return client.post<InspectionRecord[]>(
        API_ROUTES.quality.records(taskId),
        { records }
      )
    },
    
    /**
     * 完成检验任务
     */
    completeTask(id: number, data?: CompleteTaskForm) {
      return client.post<InspectionTask>(API_ROUTES.quality.complete(id), data)
    },
    
    /**
     * 让步接收
     */
    concessionTask(id: number, data: ConcessionForm) {
      return client.post<InspectionTask>(API_ROUTES.quality.concession(id), data)
    },
    
    /**
     * 取消检验任务
     */
    cancelTask(id: number, reason?: string) {
      return client.post<InspectionTask>(API_ROUTES.quality.cancel(id), { reason })
    },
    
    /**
     * 分配检验任务
     */
    assignTask(id: number, assignedTo: number) {
      return client.post<InspectionTask>(
        API_ROUTES.quality.assign(id),
        { assigned_to: assignedTo }
      )
    },
    
    /**
     * 新增检验样本数量
     *
     * 检验员在检验过程中发现质量问题，需要增加抽检数量时调用
     * 约束：新增数量 + 已有抽样数量 ≤ 批次总量
     * @param id 任务ID
     * @param additionalQuantity 新增的样本数量
     */
    addSamples(id: number, additionalQuantity: number) {
      return client.post<InspectionTask>(
        API_ROUTES.quality.addSamples(id),
        { additional_quantity: additionalQuantity }
      )
    },

    /**
     * 重置任务状态为待检验
     *
     * 将检验中的任务重置为待检验状态，同时删除所有已录入的检验记录并重新创建
     * 符合 ISO 9001 要求：只允许重置未完成的任务，已完成的任务不可修改
     * @param id 任务ID
     * @param reason 重置原因（必填，用于审计追溯）
     */
    resetTaskToPending(id: number, reason: string) {
      return client.post<InspectionTask>(
        API_ROUTES.quality.resetToPending(id),
        { reason }
      )
    },

    /**
     * 刷新任务的检验项（从模板同步最新的检验项）
     *
     * 只允许刷新待检验状态的任务
     * @param id 任务ID
     */
    refreshTaskRecords(id: number) {
      return client.post<InspectionTask>(
        API_ROUTES.quality.refreshRecords(id)
      )
    },

    /**
     * 获取模板关联的任务统计
     * @param templateId 模板ID
     */
    getTemplateRelatedTasksStats(templateId: number) {
      return client.get<{
        templateId: number
        templateCode: string
        pendingCount: number
        inProgressCount: number
        completedCount: number
        cancelledCount: number
        totalCount: number
      }>(API_ROUTES.quality.templatesRelatedTasksStats(templateId))
    },

    /**
     * 同步待检验任务的检验项
     *
     * 当模板更新后，可以手动调用此方法同步所有使用该模板且状态为"待检验"的任务
     * 检验中和已完成的任务不会被同步
     * @param templateId 模板ID
     */
    syncTemplatePendingTasks(templateId: number) {
      return client.post<{
        templateId: number
        templateCode: string
        syncedCount: number
        skippedCount: number
        syncedTasks: Array<{
          taskId: number
          taskNo: string
          oldRecordsCount: number
          newRecordsCount: number
        }>
        skippedTasks: Array<{
          taskId: number
          taskNo: string
          status: string
          reason: string
        }>
      }>(API_ROUTES.quality.syncTasks(templateId))
    },

    // ============================================================================
    // NCR 不合格品处理 API
    // ============================================================================
    
    /**
     * 获取 NCR 列表（分页）
     */
    getNCRList(params?: NCROrderQueryParams) {
      return client.get<PaginationResult<NCROrder>>(
        API_ROUTES.quality.ncrOrders,
        { params }
      )
    },
    
    /**
     * 获取待处理 NCR
     */
    getPendingNCRs(params?: NCROrderQueryParams) {
      return client.get<PaginationResult<NCROrder>>(
        API_ROUTES.quality.ncrOrdersPending,
        { params }
      )
    },
    
    /**
     * 获取 NCR 统计
     * @param params.start_date - 开始日期 (YYYY-MM-DD)
     * @param params.end_date - 结束日期 (YYYY-MM-DD)
     * @param params.source_type - 来源类型，多个用逗号分隔 (如 IQC,IPQC)
     * @param params.responsibility_type - 责任归属类型 (SUPPLIER/INTERNAL/CUSTOMER)
     */
    getNCRStatistics(params?: { 
      start_date?: string
      end_date?: string
      source_type?: string
      responsibility_type?: string
    }) {
      return client.get<{
        total: number
        pending: number
        processing: number
        closed: number
        draft: number
        pendingConfirm: number
        pendingReview: number
        pendingApproval: number
        approved: number
        rejected: number
        executed: number
        cancelled: number
        qrqcCount: number
        qrqcOverdue: number
        bySource: Record<string, number>
        byLevel: Record<string, number>
        byDefectLevel: Record<string, number>
        byDisposition: Record<string, number>
        byResponsibility: Record<string, number>
      }>(API_ROUTES.quality.ncrOrdersStatistics, { params })
    },
    
    /**
     * 获取 NCR 趋势
     */
    getNCRTrend(params?: { start_date?: string; end_date?: string }) {
      return client.get<{ date: string; count: number }[]>(
        API_ROUTES.quality.trend,
        { params }
      )
    },
    
    /**
     * 获取 NCR 详情
     */
    getNCRDetail(id: number) {
      return client.get<NCROrder>(API_ROUTES.quality.qualityNcrOrders(id))
    },
    
    /**
     * 创建 NCR
     */
    createNCR(data: CreateNCROrderForm) {
      return client.post<NCROrder>(API_ROUTES.quality.ncrOrders, data)
    },
    
    /**
     * 更新 NCR（仅草稿状态可编辑）
     */
    updateNCR(id: number, data: Partial<CreateNCROrderForm>) {
      return client.put<NCROrder>(API_ROUTES.quality.qualityNcrOrders(id), data)
    },

    /**
     * 提交评审（草稿 -> 待评审）
     */
    submitNCRForReview(id: number) {
      return client.post<NCROrder>(API_ROUTES.quality.submitForReview(id))
    },

    /**
     * 确认接收（待确认 -> 待评审，记录确认人与确认时间）
     *
     * 仅单据责任人本人可确认
     */
    confirmNCR(id: number) {
      return client.post<NCROrder>(API_ROUTES.quality.ncrOrdersConfirm(id))
    },

    /**
     * 撤回待确认（待确认 -> 草稿，清空责任人）
     *
     * 仅单据创建人在责任人确认接收前可撤回，撤回后可重新编辑再提交
     */
    withdrawNCR(id: number) {
      return client.post<NCROrder>(API_ROUTES.quality.ncrOrdersWithdraw(id))
    },

    /**
     * 隔离登记（D3）：数量按批次转入系统内置隔离库并锁定
     *
     * batch_id 为来源库存批次；数量支持部分隔离，成功后目标批次为 QUARANTINE+锁定
     */
    isolateNCR(id: number, data: { batch_id: number, quantity: number }) {
      return client.post<NCROrder>(API_ROUTES.quality.ncrOrdersIsolate(id), data)
    },

    /**
     * 登记损失金额（D14 实装）
     *
     * loss_amount 传 null 清空；仅非取消态可登记
     */
    setNCRLoss(id: number, data: { loss_amount: number | null, loss_currency?: string | null }) {
      return client.post<NCROrder>(API_ROUTES.quality.ncrOrdersLoss(id), data)
    },

    /**
     * 分配 NCR 处理人
     *
     * handler_employee_id 传 mes_employees.id（员工选择器语义），后端反查系统账号；
     * handler_id 为 users.id 账号语义，两者二选一
     */
    assignNCR(id: number, data: { handler_id?: number; handler_employee_id?: number }) {
      return client.post<NCROrder>(
        API_ROUTES.quality.ncrOrdersAssign(id),
        data
      )
    },
    
    /**
     * 提交 NCR 处置方案
     */
    submitNCRDisposition(id: number, data: NCRDispositionForm) {
      return client.post<NCROrder>(API_ROUTES.quality.disposition(id), data)
    },
    
    /**
     * 审批 NCR
     */
    approveNCR(id: number, approved: boolean, remark?: string) {
      return client.post<NCROrder>(
        API_ROUTES.quality.approveApplication(id),
        { approved, remark }
      )
    },
    
    /**
     * 执行 NCR 处置
     */
    executeNCR(id: number, data: NCRExecuteForm) {
      return client.post<NCROrder>(API_ROUTES.quality.execute(id), data)
    },
    
    /**
     * 关闭 NCR
     */
    closeNCR(id: number, remark?: string) {
      return client.post<NCROrder>(API_ROUTES.quality.close(id), { remark })
    },
    
    /**
     * 取消 NCR
     */
    cancelNCR(id: number, reason: string) {
      return client.post<NCROrder>(
        API_ROUTES.quality.ncrOrdersCancel(id),
        { reason }
      )
    },
    
    /**
     * 获取 NCR 操作日志
     */
    getNCRLogs(id: number) {
      return client.get<any[]>(API_ROUTES.quality.ncrOrdersLogs(id))
    },
    
    /**
     * 添加 NCR 备注
     */
    addNCRRemark(id: number, content: string) {
      return client.post<any>(API_ROUTES.quality.remarks(id), { content })
    },
    
    /**
     * 获取来源类型选项
     */
    getNCRSourceTypes() {
      return client.get<{ value: string; label: string }[]>(
        API_ROUTES.quality.ncrOrdersSourceTypes
      )
    },
    
    /**
     * 获取缺陷等级选项
     */
    getNCRDefectLevels() {
      return client.get<{ value: string; label: string }[]>(
        API_ROUTES.quality.ncrOrdersDefectLevels
      )
    },
    
    /**
     * 获取处置方式选项
     */
    getNCRDispositions() {
      return client.get<{ value: string; label: string }[]>(
        API_ROUTES.quality.ncrOrdersDispositions
      )
    },
    
    // ============================================================================
    // QRQC 升级 API
    // ============================================================================
    
    /**
     * 将 NCR 升级为 QRQC
     * @param id NCR ID
     * @param data QRQC 表单数据（级别、根因分析、纠正/预防措施）
     */
    escalateToQRQC(id: number, data: EscalateToQRQCForm) {
      return client.post<NCROrder>(
        API_ROUTES.quality.escalateQrqc(id),
        data
      )
    },
    
    /**
     * 更新 QRQC 信息
     */
    updateQRQC(id: number, data: Partial<EscalateToQRQCForm>) {
      return client.put<NCROrder>(
        API_ROUTES.quality.ncrOrdersQrqc(id),
        data
      )
    },
    
    /**
     * 提交 QRQC 验证结果
     */
    verifyQRQC(id: number, data: { verification_result: string; passed: boolean }) {
      return client.post<NCROrder>(
        API_ROUTES.quality.verifyQrqc(id),
        data
      )
    },

    // ============================================================================
    // 不良原因库 API
    // ============================================================================

    /**
     * 不良原因列表（管理端，分页）
     */
    getDefectReasonList(params?: DefectReasonQueryParams) {
      return client.get<PaginationResult<DefectReason>>(
        API_ROUTES.quality.defectReasons,
        { params }
      )
    },

    /**
     * 启用不良原因全量（选择器用，不分页）
     */
    getDefectReasonOptions() {
      return client.get<DefectReason[]>(API_ROUTES.quality.defectReasonsOptions)
    },

    /**
     * 创建不良原因
     */
    createDefectReason(data: DefectReasonStoreForm) {
      return client.post<DefectReason>(API_ROUTES.quality.defectReasons, data)
    },

    /**
     * 批量创建不良原因（按名称幂等）
     */
    batchCreateDefectReasons(data: DefectReasonBatchCreateForm) {
      return client.post<DefectReasonBatchCreateResult>(API_ROUTES.quality.defectReasonsBatch, data)
    },

    /**
     * 更新不良原因
     */
    updateDefectReason(id: number, data: DefectReasonUpdateForm) {
      return client.put<DefectReason>(API_ROUTES.quality.defectReasonsItem(id), data)
    },

    /**
     * 删除不良原因（软删）
     */
    deleteDefectReason(id: number) {
      return client.delete<null>(API_ROUTES.quality.defectReasonsItem(id))
    },

    /**
     * 现场快速创建不良原因（移动端，按名称幂等）
     */
    quickCreateDefectReason(data: DefectReasonQuickCreateForm) {
      return client.post<DefectReason>(API_ROUTES.quality.mobileDefectReasons, data)
    },
    
    // ============================================================================
    // AQL 标准 API
    // ============================================================================
    
    /**
     * 获取 AQL 标准列表
     */
    getAQLStandardList(params?: AQLStandardQueryParams) {
      return client.get<PaginationResult<any>>(
        API_ROUTES.quality.aqlStandards,
        { params }
      )
    },
    
    /**
     * 获取所有 AQL 标准
     */
    getAllAQLStandards() {
      return client.get<any[]>(API_ROUTES.quality.aqlStandardsAll)
    },
    
    /**
     * 获取 AQL 等级选项
     */
    getAQLLevels() {
      return client.get<{ value: string; label: string }[]>(
        API_ROUTES.quality.aqlStandardsLevels
      )
    },
    
    /**
     * 获取 AQL 值选项
     */
    getAQLValues() {
      return client.get<number[]>(API_ROUTES.quality.aqlStandardsAqlValues)
    },
    
    /**
     * 计算样本量
     */
    calculateSampleSize(params: { lot_size: number; level: string; aql?: number }) {
      return client.get<{ sample_size: number; accept: number; reject: number }>(
        API_ROUTES.quality.aqlStandardsCalculateSample,
        { params }
      )
    },
    
    /**
     * 判定批次
     */
    judgeLot(params: { sample_size: number; defect_count: number; aql: number }) {
      return client.get<{ result: 'ACCEPT' | 'REJECT'; accept: number; reject: number }>(
        API_ROUTES.quality.judgeLot,
        { params }
      )
    },
    
    // ============================================================================
    // 自检配置 API
    // ============================================================================
    
    /**
     * 获取自检配置列表
     */
    getSelfInspectionConfigList(params?: SelfInspectionConfigQueryParams) {
      return client.get<PaginationResult<SelfInspectionConfig>>(
        API_ROUTES.quality.selfInspectionConfigs,
        { params }
      )
    },
    
    /**
     * 获取自检配置详情
     */
    getSelfInspectionConfigDetail(id: number) {
      return client.get<SelfInspectionConfig>(
        API_ROUTES.quality.qualitySelfInspectionConfigs(id)
      )
    },
    
    /**
     * 创建自检配置
     */
    createSelfInspectionConfig(data: CreateSelfInspectionConfigForm) {
      return client.post<SelfInspectionConfig>(
        API_ROUTES.quality.selfInspectionConfigs,
        data
      )
    },
    
    /**
     * 更新自检配置
     */
    updateSelfInspectionConfig(id: number, data: Partial<CreateSelfInspectionConfigForm>) {
      return client.put<SelfInspectionConfig>(
        API_ROUTES.quality.qualitySelfInspectionConfigs(id),
        data
      )
    },
    
    /**
     * 删除自检配置
     */
    deleteSelfInspectionConfig(id: number) {
      return client.delete<void>(API_ROUTES.quality.qualitySelfInspectionConfigs(id))
    },
    
    /**
     * 更新自检配置状态
     */
    updateSelfInspectionConfigStatus(id: number, status: number) {
      return client.put<SelfInspectionConfig>(
        API_ROUTES.quality.selfInspectionConfigsStatus(id),
        { status }
      )
    },
    
    /**
     * 获取频次类型选项
     */
    getFrequencyTypes() {
      return client.get<{ value: string; label: string }[]>(
        API_ROUTES.quality.selfInspectionConfigsFrequencyTypes
      )
    },
    
    /**
     * 获取时间单位选项
     */
    getTimeUnits() {
      return client.get<{ value: string; label: string }[]>(
        API_ROUTES.quality.selfInspectionConfigsTimeUnits
      )
    },
    
    // ============================================================================
    // IQC 来料检验 API
    // ============================================================================
    
    /**
     * 获取待检收货列表
     */
    getPendingReceivings(params?: { page?: number; per_page?: number }) {
      return client.get<PaginationResult<any>>(
        API_ROUTES.quality.iqcPendingReceivings,
        { params }
      )
    },
    
    /**
     * 为收货创建 IQC 任务
     */
    createIQCTask(receivingId: number) {
      return client.post<InspectionTask>(
        API_ROUTES.quality.createTask(receivingId)
      )
    },
    
    /**
     * 批量创建 IQC 任务
     */
    batchCreateIQCTasks(receivingIds: number[]) {
      return client.post<{ success: number; failed: number }>(
        API_ROUTES.quality.batchCreateTasks,
        { receiving_ids: receivingIds }
      )
    },
    
    /**
     * 获取 IQC 任务列表
     */
    getIQCTasks(params?: InspectionTaskQueryParams) {
      return client.get<PaginationResult<InspectionTask>>(
        API_ROUTES.quality.iqcTasks,
        { params }
      )
    },
    
    /**
     * 获取 IQC 统计
     */
    getIQCStatistics() {
      // 键与后端 IQCController::statistics 返回一致
      return client.get<{
        pendingReceivings: number
        totalTasks: number
        completedTasks: number
        pendingTasks: number
        qualifiedTasks: number
        rejectedTasks: number
        passRate: number
        dateRange: { start: string; end: string }
      }>(API_ROUTES.quality.iqcStatistics)
    },
    
    // ============================================================================
    // 质量看板 API
    // ============================================================================
    
    /**
     * 获取看板概览
     */
    getDashboardOverview() {
      return client.get<{
        today: {
          tasks: {
            total: number
            completed: number
            pending: number
            qualified: number
            rejected: number
          }
          passRate: number
          ncrCount: number
        }
        month: {
          tasks: {
            total: number
            completed: number
            qualified: number
            rejected: number
          }
          passRate: number
        }
        pendingNcrs: number
        updatedAt: string
      }>(API_ROUTES.quality.dashboardOverview)
    },
    
    /**
     * 获取 KPI 指标
     */
    getDashboardKPI() {
      return client.get<{
        fpyRate: number
        iqcPassRate: number
        ipqcPassRate: number
        ncrRate: number
        avgInspectionMinutes: number
        totalTasks: number
        totalNcrs: number
        dateRange: {
          start: string
          end: string
        }
      }>(API_ROUTES.quality.dashboardKpi)
    },
    
    /**
     * 获取任务类型分布
     */
    getTaskTypeDistribution() {
      return client.get<{ type: string; name: string; count: number }[]>(
        API_ROUTES.quality.dashboardTaskTypeDistribution
      )
    },
    
    /**
     * 获取合格率趋势
     */
    getPassRateTrend(params?: { days?: number }) {
      return client.get<{ date: string; total: number; qualified: number; passRate: number }[]>(
        API_ROUTES.quality.dashboardPassRateTrend,
        { params }
      )
    },
    
    /**
     * 获取缺陷分布
     */
    getDefectDistribution() {
      return client.get<{ itemCode: string; itemName: string; count: number }[]>(
        API_ROUTES.quality.dashboardDefectDistribution
      )
    },
    
    /**
     * 获取工位质量状态
     */
    getWorkstationStatus() {
      return client.get<{
        workstationId: number
        workstationCode: string
        workstationName: string
        lineName: string
        qualityCheckType: string
        operatorName: string
        pendingTasks: number
        lastInspection: {
          taskNo: string
          taskType: string
          result: string
          completedAt: string | null
        } | null
      }[]>(API_ROUTES.quality.dashboardWorkstationStatus)
    },
    
    /**
     * 获取检验员绩效
     */
    getInspectorPerformance() {
      return client.get<{
        inspectorId: number
        inspectorName: string
        totalTasks: number
        qualifiedTasks: number
        passRate: number
        avgDurationMinutes: number
      }[]>(API_ROUTES.quality.dashboardInspectorPerformance)
    },
    
    /**
     * 获取质量预警
     */
    getDashboardAlerts() {
      return client.get<{
        items: Array<{
          type: string
          level: string
          title: string
          message: string
          data: Record<string, any>
          createdAt: string
        }>
        total: number
        updatedAt: string
      }>(API_ROUTES.quality.dashboardAlerts)
    },

    // ============================================================================
    // 质量检验汇总看板 API（历史数据分析型）
    // ============================================================================

    /**
     * 获取时间范围选项
     * 
     * 返回可用的快速时间选择选项，用于前端渲染时间选择器
     */
    getDateRangeOptions() {
      return client.get<DateRangeOptionsResponse>(
        API_ROUTES.quality.inspectionSummaryDateRangeOptions
      )
    },

    /**
     * 获取指标解释
     * 
     * 返回各指标的计算公式和含义说明，用于前端Tooltip展示
     */
    getMetricExplanations() {
      return client.get<MetricExplanationsResponse>(
        API_ROUTES.quality.inspectionSummaryMetricExplanations
      )
    },

    /**
     * 获取综合汇总
     * 
     * 获取指定时间范围内的检验总体数据，包括任务统计、合格率、NCR统计和环比变化
     */
    getInspectionSummary(params?: InspectionSummaryQueryParams) {
      return client.get<SummaryResponse>(
        API_ROUTES.quality.summary,
        { params }
      )
    },

    /**
     * 按检验类型分析
     * 
     * 按IQC/IPQC/FQC/OQC等检验类型统计任务数量、合格率和NCR数量
     */
    getInspectionSummaryByTaskType(params?: InspectionSummaryQueryParams) {
      return client.get<TaskTypeAnalysisResponse>(
        API_ROUTES.quality.inspectionSummaryByTaskType,
        { params }
      )
    },

    /**
     * 按产品分析
     * 
     * 按产品统计检验数据和合格率，支持分页和排序
     */
    getInspectionSummaryByProduct(params?: PaginatedQueryParams) {
      return client.get<ProductAnalysisResponse>(
        API_ROUTES.quality.inspectionSummaryByProduct,
        { params }
      )
    },

    /**
     * 按物料分析
     * 
     * 按物料统计来料检验数据，支持分页
     */
    getInspectionSummaryByMaterial(params?: PaginatedQueryParams) {
      return client.get<MaterialAnalysisResponse>(
        API_ROUTES.quality.inspectionSummaryByMaterial,
        { params }
      )
    },

    /**
     * 按供应商分析
     * 
     * 按供应商统计来料质量，主要针对IQC检验任务
     */
    getInspectionSummaryBySupplier(params?: PaginatedQueryParams) {
      return client.get<SupplierAnalysisResponse>(
        API_ROUTES.quality.inspectionSummaryBySupplier,
        { params }
      )
    },

    /**
     * 按质检员分析
     * 
     * 按检验员统计工作量和发现不合格率，支持分页
     */
    getInspectionSummaryByInspector(params?: PaginatedQueryParams) {
      return client.get<InspectorAnalysisResponse>(
        API_ROUTES.quality.inspectionSummaryByInspector,
        { params }
      )
    },

    /**
     * 按工位分析
     * 
     * 按工位统计过程检验数据
     */
    getInspectionSummaryByWorkstation(params?: InspectionSummaryQueryParams) {
      return client.get<WorkstationAnalysisResponse>(
        API_ROUTES.quality.inspectionSummaryByWorkstation,
        { params }
      )
    },

    /**
     * 按车间分析
     * 
     * 按车间统计检验数据（通过工位关联）
     */
    getInspectionSummaryByWorkshop(params?: InspectionSummaryQueryParams) {
      return client.get<WorkshopAnalysisResponse>(
        API_ROUTES.quality.inspectionSummaryByWorkshop,
        { params }
      )
    },

    /**
     * 缺陷TOP分析
     * 
     * 按不同维度（检验项/产品/物料/供应商）统计缺陷排名
     */
    getInspectionSummaryDefectTop(params?: DefectTopQueryParams) {
      return client.get<DefectTopResponse>(
        API_ROUTES.quality.inspectionSummaryDefectTop,
        { params }
      )
    },

    /**
     * 趋势分析
     * 
     * 获取合格率、NCR数量等指标的时间趋势
     */
    getInspectionSummaryTrend(params?: TrendQueryParams) {
      return client.get<TrendResponse>(
        API_ROUTES.quality.inspectionSummaryTrend,
        { params }
      )
    },

    /**
     * 单产品详情
     * 
     * 获取单个产品的详细质量数据，包括各检验类型统计、缺陷分布、趋势和供应商质量
     */
    getInspectionSummaryProductDetail(productId: number, params?: InspectionSummaryQueryParams) {
      return client.get<ProductDetailResponse>(
        API_ROUTES.quality.inspectionSummaryProductDetail(productId),
        { params }
      )
    },

    /**
     * 单供应商详情
     * 
     * 获取单个供应商的详细质量数据，包括物料质量分布和趋势
     */
    getInspectionSummarySupplierDetail(supplierId: number, params?: InspectionSummaryQueryParams) {
      return client.get<SupplierDetailResponse>(
        API_ROUTES.quality.inspectionSummarySupplierDetail(supplierId),
        { params }
      )
    },

    /**
     * NCR汇总分析
     * 
     * 获取NCR数量、类型、处置方式分析
     */
    getInspectionSummaryNcrSummary(params?: InspectionSummaryQueryParams) {
      return client.get<NcrSummaryResponse>(
        API_ROUTES.quality.inspectionSummaryNcrSummary,
        { params }
      )
    },

    /**
     * 刷新缓存
     * 
     * 手动刷新统计数据缓存，用于需要立即查看最新数据的场景
     */
    refreshInspectionSummaryCache() {
      return client.post<{ message: string }>(
        API_ROUTES.quality.refreshCache
      )
    },

    // ========== 员工自检相关 API ==========

    /**
     * 获取我的待自检任务列表
     * @param params.workstation_id - 工位ID（可选）
     * @param params.work_order_id - 工单ID（可选）
     */
    getMySelfInspectionPending(params?: {
      workstation_id?: number
      work_order_id?: number
    }) {
      return client.get<{
        items: Array<{
          id: number
          taskNo: string
          productCode: string
          productName: string
          workOrderNo: string
          operationName: string
          workstationCode: string
          lotQuantity: number
          sampleQuantity: number
          status: string
          statusName: string
          createdAt: string
        }>
        total: number
      }>(API_ROUTES.quality.selfInspectionPending, { params })
    },

    /**
     * 获取自检记录列表（支持多维度筛选）
     * @param params.user_id - 检验人ID（管理员可查看所有人）
     * @param params.workstation_id - 工位ID
     * @param params.work_order_id - 工单ID
     * @param params.product_id - 产品ID
     * @param params.start_date - 开始日期
     * @param params.end_date - 结束日期
     * @param params.result - 检验结果（QUALIFIED/REJECTED/CONDITIONAL）
     * @param params.page - 页码
     * @param params.per_page - 每页数量
     */
    getSelfInspectionRecords(params?: {
      user_id?: number
      workstation_id?: number
      work_order_id?: number
      product_id?: number
      start_date?: string
      end_date?: string
      result?: string
      page?: number
      per_page?: number
    }) {
      return client.get<{
        items: Array<{
          id: number
          taskNo: string
          productCode: string
          productName: string
          workOrderNo: string
          operationName: string
          workstationCode: string
          inspectorId: number
          inspectorName: string
          lotQuantity: number
          sampleQuantity: number
          result: string
          resultName: string
          qualifiedItems: number
          rejectedItems: number
          completedAt: string
        }>
        total: number
        currentPage: number
        lastPage: number
      }>(API_ROUTES.quality.selfInspectionRecords, { params })
    },

    /**
     * 获取自检率统计
     * @param params.dimension - 统计维度（personal/workstation/work_order/product）
     * @param params.user_id - 用户ID（维度为personal时）
     * @param params.workstation_id - 工位ID
     * @param params.work_order_id - 工单ID
     * @param params.product_id - 产品ID
     * @param params.start_date - 开始日期
     * @param params.end_date - 结束日期
     */
    getSelfInspectionRate(params?: {
      dimension?: 'personal' | 'workstation' | 'work_order' | 'product'
      user_id?: number
      workstation_id?: number
      work_order_id?: number
      product_id?: number
      start_date?: string
      end_date?: string
    }) {
      return client.get<{
        dimension: string
        items: Array<{
          inspectorId?: number
          inspectorName?: string
          workstationId?: number
          workstationCode?: string
          workOrderId?: number
          workOrderNo?: string
          productId?: number
          productCode?: string
          productName?: string
          total: number
          qualified: number
          rejected: number
          passRate: number
        }>
      }>(API_ROUTES.quality.rate, { params })
    },

    /**
     * 主动触发自检任务
     * @param data.work_order_id - 工单ID（必填）
     * @param data.workstation_id - 工位ID（必填）
     */
    triggerSelfInspection(data: {
      work_order_id: number
      workstation_id: number
    }) {
      return client.post<{
        taskId: number
        taskNo: string
        message: string
      }>(API_ROUTES.quality.selfInspectionTrigger, data)
    },

    // ============================================================================
    // 8D报告管理 API（32个路由）
    // ============================================================================

    /**
     * 获取8D报告列表（分页）
     */
    getEightDReportList(params?: EightDReportQueryParams) {
      return client.get<PaginationResult<EightDReportListItem>>(
        API_ROUTES.quality['8dReports'],
        { params }
      )
    },

    /**
     * 获取8D报告详情
     */
    getEightDReportDetail(id: number) {
      return client.get<EightDReport>(API_ROUTES.quality.quality8dReports(id))
    },

    /**
     * 创建8D报告
     */
    createEightDReport(data: CreateEightDReportForm) {
      return client.post<EightDReport>(API_ROUTES.quality['8dReports'], data)
    },

    /**
     * 更新8D报告
     */
    updateEightDReport(id: number, data: UpdateEightDReportForm) {
      return client.put<EightDReport>(API_ROUTES.quality.quality8dReports(id), data)
    },

    /**
     * 删除8D报告
     */
    deleteEightDReport(id: number) {
      return client.delete<void>(API_ROUTES.quality.quality8dReports(id))
    },

    /**
     * 更新D0步骤
     */
    updateEightDD0(id: number, data: UpdateEightDD0Form) {
      return client.put<EightDReport>(API_ROUTES.quality.stepD0(id), data)
    },

    /**
     * 更新D1步骤
     */
    updateEightDD1(id: number, data: UpdateEightDD1Form) {
      return client.put<EightDReport>(API_ROUTES.quality.stepD1(id), data)
    },

    /**
     * 更新D2步骤
     */
    updateEightDD2(id: number, data: UpdateEightDD2Form) {
      return client.put<EightDReport>(API_ROUTES.quality.stepD2(id), data)
    },

    /**
     * 更新D3步骤
     */
    updateEightDD3(id: number, data: UpdateEightDD3Form) {
      return client.put<EightDReport>(API_ROUTES.quality.stepD3(id), data)
    },

    /**
     * 更新D4步骤
     */
    updateEightDD4(id: number, data: UpdateEightDD4Form) {
      return client.put<EightDReport>(API_ROUTES.quality.stepD4(id), data)
    },

    /**
     * 更新D5步骤
     */
    updateEightDD5(id: number, data: UpdateEightDD5Form) {
      return client.put<EightDReport>(API_ROUTES.quality.stepD5(id), data)
    },

    /**
     * 更新D6步骤
     */
    updateEightDD6(id: number, data: UpdateEightDD6Form) {
      return client.put<EightDReport>(API_ROUTES.quality.stepD6(id), data)
    },

    /**
     * 更新D7步骤
     */
    updateEightDD7(id: number, data: UpdateEightDD7Form) {
      return client.put<EightDReport>(API_ROUTES.quality.stepD7(id), data)
    },

    /**
     * 更新D8步骤
     */
    updateEightDD8(id: number, data: UpdateEightDD8Form) {
      return client.put<EightDReport>(API_ROUTES.quality.stepD8(id), data)
    },

    /**
     * 步骤回退
     */
    eightDStepBackward(id: number, data: EightDStepBackwardForm) {
      return client.post<EightDReport>(API_ROUTES.quality.stepBackward(id), data)
    },

    /**
     * 添加团队成员
     */
    addEightDTeamMember(id: number, data: AddEightDTeamMemberForm) {
      return client.post<EightDTeamMember>(API_ROUTES.quality.teamMembers(id), data)
    },

    /**
     * 批量添加团队成员（团队组建工作台；后端事务，重复整批拒绝）
     */
    addEightDTeamMembersBatch(
      id: number,
      data: AddEightDTeamMembersBatchForm,
    ) {
      return client.post<EightDTeamMember[]>(
        API_ROUTES.quality.teamMembersBatch(id),
        data,
      )
    },

    /**
     * 移除团队成员
     */
    removeEightDTeamMember(id: number, userId: number) {
      return client.delete<void>(API_ROUTES.quality['8dReportsTeamMembers'](id, userId))
    },

    /**
     * 成员确认
     */
    confirmEightDMember(id: number) {
      return client.post<{ confirmed: boolean }>(API_ROUTES.quality.confirm(id))
    },

    /**
     * 关联NCR
     */
    linkEightDNCR(id: number, data: LinkEightDNCRForm) {
      return client.post<EightDNCRLink>(API_ROUTES.quality.ncrLinks(id), data)
    },

    /**
     * 取消关联NCR
     */
    unlinkEightDNCR(id: number, ncrId: number) {
      return client.delete<void>(API_ROUTES.quality['8dReportsNcrLinks'](id, ncrId))
    },

    /**
     * 关闭8D报告
     */
    closeEightDReport(id: number, data?: CloseEightDReportForm) {
      return client.post<EightDReport>(API_ROUTES.quality['8dReportsClose'](id), data)
    },

    /**
     * 取消8D报告
     */
    cancelEightDReport(id: number, data: CancelEightDReportForm) {
      return client.post<EightDReport>(API_ROUTES.quality['8dReportsCancel'](id), data)
    },

    /**
     * 获取8D统计数据
     */
    getEightDStatistics() {
      return client.get<EightDStatistics>(API_ROUTES.quality['8dReportsStatistics'])
    },

    /**
     * 获取8D操作日志
     */
    getEightDLogs(id: number) {
      return client.get<EightDLog[]>(API_ROUTES.quality['8dReportsLogs'](id))
    },

    /**
     * 从QRQC创建8D报告
     */
    createEightDFromQRQC(ncrId: number, data?: CreateEightDFromQRQCForm) {
      return client.post<EightDReport>(API_ROUTES.quality.fromQrqc(ncrId), data)
    },

    /**
     * 获取SLA配置列表
     */
    getEightDSLAConfigs() {
      return client.get<Record<string, Record<string, EightDSLAConfig>>>(
        API_ROUTES.quality.slaConfigs
      )
    },

    /**
     * 更新SLA配置
     */
    updateEightDSLAConfig(data: UpdateEightDSLAConfigForm) {
      return client.put<EightDSLAConfig>(API_ROUTES.quality.slaConfigs, data)
    },

    /**
     * 刷新SLA缓存
     */
    refreshEightDSLACache() {
      return client.post<{ message: string }>(API_ROUTES.quality.refreshCache)
    },

    /**
     * 获取来源类型枚举
     */
    getEightDSourceTypes() {
      return client.get<Record<string, string>>(API_ROUTES.quality.enumsSourceTypes)
    },

    /**
     * 获取状态枚举
     */
    getEightDStatuses() {
      return client.get<Record<string, string>>(API_ROUTES.quality.enumsStatuses)
    },

    /**
     * 获取步骤枚举
     */
    getEightDSteps() {
      return client.get<Record<string, string>>(API_ROUTES.quality.enumsSteps)
    },

    /**
     * 获取优先级枚举
     */
    getEightDPriorities() {
      return client.get<Record<string, string>>(API_ROUTES.quality.enumsPriorities)
    },

    /**
     * 获取严重程度枚举
     */
    getEightDSeverities() {
      return client.get<Record<string, string>>(API_ROUTES.quality.enumsSeverities)
    },

    /**
     * 获取团队角色枚举
     */
    getEightDTeamRoles() {
      return client.get<Record<string, string>>(API_ROUTES.quality.enumsTeamRoles)
    },

    // ============================================================================
    // IQC 物料管理 API
    // ============================================================================

    /**
     * 获取物料免检列表
     */
    getIqcMaterials(params?: {
      keyword?: string
      material_type?: string
      skip_iqc?: boolean
      need_iqc?: boolean
      has_template?: boolean
      sort_field?: string
      sort_order?: 'asc' | 'desc'
      per_page?: number
      page?: number
    }) {
      return client.get<PaginationResult<{
        id: number
        materialCode: string
        materialName: string
        materialType: string
        materialTypeName: string
        skipIqc: boolean
        skipIqcReason: string | null
        needIqc: boolean
        iqcTemplateId: number | null
        iqcTemplateName: string | null
        iqcTemplateCode: string | null
        updatedAt: string | null
      }> & { controlMode: 'blacklist' | 'whitelist' }>(API_ROUTES.quality.materials, { params })
    },

    /**
     * 添加物料到IQC例外清单
     *
     * 根据控制模式自动设置：
     * - 白名单模式：设置 need_iqc=true
     * - 黑名单模式：设置 skip_iqc=true
     */
    addIqcMaterial(data: { material_id: number; template_id?: number; reason?: string }) {
      return client.post<{
        id: number
        materialCode: string
        materialName: string
      }>(API_ROUTES.quality.materials, data)
    },

    /**
     * 从IQC例外清单移除物料
     */
    removeIqcMaterial(materialId: number) {
      return client.delete<{
        id: number
        materialCode: string
      }>(API_ROUTES.quality.iqcMaterials(materialId))
    },

    /**
     * 批量添加物料到IQC例外清单
     */
    batchAddIqcMaterials(data: { material_ids: number[]; template_id?: number; reason?: string }) {
      return client.post<{ updated: number }>(API_ROUTES.quality.materialsBatch, data)
    },

    /**
     * 批量从IQC例外清单移除物料
     */
    batchRemoveIqcMaterials(data: { material_ids: number[] }) {
      return client.delete<{ updated: number }>(API_ROUTES.quality.materialsBatch, { data })
    },

    /**
     * 设置物料免检（旧API，保留兼容）
     */
    setMaterialSkipIqc(materialId: number, data: { skip: boolean; reason?: string }) {
      return client.put<{
        materialId: number
        materialCode: string
        skipIqc: boolean
        skipIqcReason: string | null
      }>(API_ROUTES.quality.materialsSkipIqc(materialId), data)
    },

    /**
     * 设置物料需检验（白名单模式）
     */
    setMaterialNeedIqc(materialId: number, data: { need: boolean; template_id?: number }) {
      return client.put<{
        materialId: number
        materialCode: string
        needIqc: boolean
        iqcTemplateId: number | null
      }>(API_ROUTES.quality.materialsNeedIqc(materialId), data)
    },

    /**
     * 批量设置物料免检
     */
    batchSetMaterialSkipIqc(data: { material_ids: number[]; skip: boolean; reason?: string }) {
      return client.put<{ updated: number; skip: boolean }>(API_ROUTES.quality.batchSkip, data)
    },

    /**
     * 批量设置物料需检验
     */
    batchSetMaterialNeedIqc(data: { material_ids: number[]; need: boolean; template_id?: number }) {
      return client.put<{ updated: number; need: boolean }>(API_ROUTES.quality.batchNeed, data)
    },

    /**
     * 检查物料免检状态
     */
    checkMaterialSkipIqc(materialId: number) {
      return client.get<{
        materialId: number
        materialCode: string | null
        skipIqc: boolean
        skipIqcReason: string | null
      }>(API_ROUTES.quality.materialsSkipCheck(materialId))
    },

    /**
     * 获取IQC控制模式配置
     */
    getIqcControlMode() {
      return client.get<{
        mode: 'blacklist' | 'whitelist'
        modeLabel: string
        description: string
        statistics: {
          skipIqcCount: number
          needIqcCount: number
        }
      }>(API_ROUTES.quality.controlMode)
    },

    /**
     * 设置IQC控制模式
     *
     * @param data.mode - 控制模式：blacklist(黑名单) / whitelist(白名单)
     * @param data.clear_data - 是否清除已配置的数据
     */
    setIqcControlMode(data: { mode: 'blacklist' | 'whitelist'; clear_data?: boolean }) {
      return client.put<{
        mode: 'blacklist' | 'whitelist'
        modeLabel: string
        cleared: boolean
        clearedCount: number
        message: string
      }>(API_ROUTES.quality.controlMode, data)
    },

    // ============================================================================
    // 快速检验 API
    // ============================================================================

    /**
     * 执行快速检验
     *
     * 一次性完成：创建任务 + 录入数据 + 完成检验
     * 适用场景：巡检、首件检验、临时抽检
     */
    executeQuickInspection(data: {
      inspection_type: 'PATROL' | 'FIRST_ARTICLE' | 'SPOT_CHECK' | 'RECHECK'
      template_id?: number
      work_order_id?: number
      work_order_operation_id?: number
      product_id?: number
      operation_id?: number
      batch_no: string
      lot_quantity?: number
      sample_quantity?: number
      records: Array<{
        template_item_id: number
        actual_value?: string
        actual_numeric?: number
        remark?: string
      }>
      result?: 'QUALIFIED' | 'REJECTED' | 'CONCESSION'
      remark?: string
      attachments?: string[]
      auto_create_ncr?: boolean
    }) {
      return client.post<InspectionTask>(API_ROUTES.quality.quickInspection, data)
    },

    /**
     * 获取快速检验模板建议
     */
    getQuickInspectionTemplateSuggestions(params: {
      inspection_type: string
      work_order_id?: number
      product_id?: number
      operation_id?: number
    }) {
      return client.get<{
        recommended: {
          id: number
          templateCode: string
          templateName: string
          matchType: string
        } | null
        alternatives: Array<{
          id: number
          templateCode: string
          templateName: string
          matchType: string
        }>
      }>(API_ROUTES.quality.templatesSuggestions, { params })
    },

    /**
     * 获取快速检验类型列表
     */
    getQuickInspectionTypes() {
      return client.get<Array<{ value: string; label: string }>>(API_ROUTES.quality.quickInspectionTypes)
    },
  }
}

// ============================================================================
// LPA 类型导入
// ============================================================================

import type {
  LpaLayerConfig,
  LpaLayerConfigQueryParams,
  LpaTemplate,
  LpaItem,
  LpaSchedule,
  LpaTask,
  LpaResult,
  LpaFinding,
  EightDLpaConfig,
  EightDLpaStatus,
  LpaOverviewStatistics,
  LpaCompletionStatisticsItem,
  LpaFindingStatistics,
  LpaTrendStatistics,
  LpaLayerComparisonStatistics,
  LpaEightDTaskStatistics,
  CreateLpaTemplateRequest,
  UpdateLpaTemplateRequest,
  CreateLpaLayerConfigRequest,
  UpdateLpaLayerConfigRequest,
  CreateLpaItemRequest,
  CreateLpaScheduleRequest,
  CreateLpaTaskRequest,
  SubmitLpaResultRequest,
  BatchSubmitLpaResultsRequest,
  UpdateLpaFindingRequest,
  AssignLpaFindingRequest,
  VerifyLpaFindingRequest,
  CancelLpaFindingRequest,
  UpdateEightDLpaConfigRequest,
  AddEightDLpaItemRequest,
  LpaTemplateQueryParams,
  LpaScheduleQueryParams,
  LpaTaskQueryParams,
  LpaFindingQueryParams,
  LpaStatisticsQueryParams,
  LpaMonthlySheetQueryParams,
  LpaMonthlySheetResponse,
  LpaTaskItem,
  LpaModuleConfig,
  UpdateLpaConfigRequest,
  LpaAuditorRoster,
  LpaRosterQueryParams,
  CreateLpaRosterRequest,
  BatchCreateLpaRostersRequest,
  LpaRosterPrefillCandidate,
  ReviewLpaTaskRequest,
  LpaTemplateVersionInfo,
  CreateLpaTemplateVersionRequest,
  LpaLibraryItem,
  LpaLibraryItemQueryParams,
  CreateLpaLibraryItemRequest,
  UpdateLpaLibraryItemRequest,
} from '../../types/quality'

// ============================================================================
// LPA API 工厂函数
// ============================================================================

/**
 * 创建 LPA 分层审核 API 实例
 * 
 * @description 使用从后端导出的 API_ROUTES.quality 路由
 * @param client - HTTP 请求客户端
 * @returns LPA API 实例
 */
export function createLpaApi(client: IRequestClient) {
  const routes = API_ROUTES.quality

  return {
    // ========================================================================
    // 层级配置
    // ========================================================================

    /** 获取所有层级配置（支持服务端排序白名单，见 LpaLayerConfigQueryParams） */
    getLayerConfigs(params?: LpaLayerConfigQueryParams) {
      return client.get<LpaLayerConfig[]>(routes.layerConfigs, { params })
    },

    /** 获取启用的层级配置 */
    getActiveLayerConfigs() {
      return client.get<LpaLayerConfig[]>(routes.active)
    },

    /** 获取频率类型枚举 */
    getFrequencyTypes() {
      return client.get<Record<string, string>>(routes.layerConfigsFrequencyTypes)
    },

    /** 创建层级配置（snake_case 写入口径，复用响应类型会 422，见审查 F13） */
    createLayerConfig(data: CreateLpaLayerConfigRequest) {
      return client.post<LpaLayerConfig>(routes.layerConfigs, data)
    },

    /** 获取层级配置详情 */
    getLayerConfig(id: number | string) {
      return client.get<LpaLayerConfig>(routes.lpaLayerConfigs(id))
    },

    /** 更新层级配置 */
    updateLayerConfig(id: number | string, data: UpdateLpaLayerConfigRequest) {
      return client.put<LpaLayerConfig>(routes.lpaLayerConfigs(id), data)
    },

    /** 删除层级配置 */
    deleteLayerConfig(id: number | string) {
      return client.delete<void>(routes.lpaLayerConfigs(id))
    },

    // ========================================================================
    // 模板管理
    // ========================================================================

    /** 获取模板列表 */
    getTemplates(params?: LpaTemplateQueryParams) {
      return client.get<PaginationResult<LpaTemplate>>(routes.lpaTemplates(0).replace('/0', ''), { params })
    },

    /** 获取区域类型枚举 */
    getAreaTypes() {
      return client.get<Record<string, string>>(routes.templatesAreaTypes)
    },

    /** 获取审核类型枚举 */
    getAuditTypes() {
      return client.get<Record<string, string>>(routes.templatesAuditTypes)
    },

    /** 创建模板 */
    createTemplate(data: CreateLpaTemplateRequest) {
      return client.post<LpaTemplate>(routes.lpaTemplates(0).replace('/0', ''), data)
    },

    /** 获取模板详情 */
    getTemplate(id: number | string) {
      return client.get<LpaTemplate>(routes.lpaTemplates(id))
    },

    /** 更新模板 */
    updateTemplate(id: number | string, data: UpdateLpaTemplateRequest) {
      return client.put<LpaTemplate>(routes.lpaTemplates(id), data)
    },

    /** 删除模板 */
    deleteTemplate(id: number | string) {
      return client.delete<void>(routes.lpaTemplates(id))
    },

    /** 复制模板 */
    copyTemplate(id: number | string, name?: string) {
      return client.post<LpaTemplate>(routes.templatesCopy(id), { name })
    },

    /** 切换模板状态 */
    toggleTemplateStatus(id: number | string) {
      return client.put<LpaTemplate>(routes.templatesToggleStatus(id))
    },

    // ========================================================================
    // 检查项管理
    // ========================================================================

    /** 获取模板检查项列表 */
    getTemplateItems(templateId: number | string) {
      return client.get<LpaItem[]>(routes.lpaTemplateItems(templateId))
    },

    /** 添加检查项 */
    addTemplateItem(templateId: number | string, data: CreateLpaItemRequest) {
      return client.post<LpaItem>(routes.lpaTemplateItems(templateId), data)
    },

    /** 更新检查项 */
    updateTemplateItem(templateId: number | string, itemId: number | string, data: Partial<CreateLpaItemRequest>) {
      return client.put<LpaItem>(routes.lpaTemplateItem(templateId, itemId), data)
    },

    /** 删除检查项 */
    deleteTemplateItem(templateId: number | string, itemId: number | string) {
      return client.delete<void>(routes.lpaTemplateItem(templateId, itemId))
    },

    /** 批量添加检查项 */
    batchAddTemplateItems(templateId: number | string, items: CreateLpaItemRequest[]) {
      return client.post<LpaItem[]>(routes.itemsBatch(templateId), { items })
    },

    /** 更新检查项排序 */
    sortTemplateItems(templateId: number | string, itemIds: number[]) {
      return client.put<void>(routes.lpaTemplateItemsSort(templateId), { item_ids: itemIds })
    },

    /**
     * 从公共检查项库批量插入检查项（快照式，返回 {created, skipped}）
     *
     * @param applicableLayers 适用层级代码；缺省时由后端回落到模板已启用层级。
     *   留空会导致插入的检查项不进入任何审核任务（任务生成按适用层级过滤）。
     */
    addItemsFromLibrary(
      templateId: number | string,
      libraryItemIds: number[],
      applicableLayers?: string[],
    ) {
      return client.post<{ created: number, skipped: number }>(
        routes.lpaTemplateItemsFromLibrary(templateId),
        { library_item_ids: libraryItemIds, applicable_layers: applicableLayers },
      )
    },

    // ========================================================================
    // 版本管理
    // ========================================================================

    /** 升版：以当前模板为蓝本创建新版本（计划与8D配置重绑新版本） */
    createTemplateVersion(templateId: number | string, data: CreateLpaTemplateVersionRequest = {}) {
      return client.post<LpaTemplate>(routes.lpaTemplateCreateVersion(templateId), data)
    },

    /** 版本历史（同 template_code 全部版本，新版本在前） */
    getTemplateVersions(templateId: number | string) {
      return client.get<LpaTemplateVersionInfo[]>(routes.lpaTemplateVersions(templateId))
    },

    // ========================================================================
    // 公共检查项库
    // ========================================================================

    /** 库条目列表 */
    getLibraryItems(params?: LpaLibraryItemQueryParams) {
      return client.get<PaginationResult<LpaLibraryItem>>(routes.lpaLibraryItems, { params })
    },

    /** 创建库条目 */
    createLibraryItem(data: CreateLpaLibraryItemRequest) {
      return client.post<LpaLibraryItem>(routes.lpaLibraryItems, data)
    },

    /** 更新库条目 */
    updateLibraryItem(id: number | string, data: UpdateLpaLibraryItemRequest) {
      return client.put<LpaLibraryItem>(routes.lpaLibraryItem(id), data)
    },

    /** 删除库条目（软删，不影响已插入模板的快照副本） */
    deleteLibraryItem(id: number | string) {
      return client.delete<void>(routes.lpaLibraryItem(id))
    },

    /** 把模板检查项沉淀入库（8D 来源项后端拒绝，返回 {created, skipped}） */
    saveItemsToLibrary(templateId: number, itemIds: number[]) {
      return client.post<{ created: number, skipped: number }>(
        routes.lpaLibraryItemsFromTemplate,
        { template_id: templateId, item_ids: itemIds },
      )
    },

    // ========================================================================
    // 计划管理
    // ========================================================================

    /** 获取计划列表 */
    getSchedules(params?: LpaScheduleQueryParams) {
      return client.get<PaginationResult<LpaSchedule>>(routes.schedules, { params })
    },

    /** 创建计划 */
    createSchedule(data: CreateLpaScheduleRequest) {
      return client.post<LpaSchedule>(routes.schedules, data)
    },

    /** 获取计划详情 */
    getSchedule(id: number | string) {
      return client.get<LpaSchedule>(routes.lpaSchedules(id))
    },

    /** 更新计划 */
    updateSchedule(id: number | string, data: Partial<CreateLpaScheduleRequest>) {
      return client.put<LpaSchedule>(routes.lpaSchedules(id), data)
    },

    /** 删除计划 */
    deleteSchedule(id: number | string) {
      return client.delete<void>(routes.lpaSchedules(id))
    },

    /** 切换计划状态 */
    toggleSchedule(id: number | string) {
      return client.put<LpaSchedule>(routes.schedulesToggle(id))
    },

    /** 手动生成任务 */
    generateScheduleTasks(id: number | string) {
      return client.post<LpaTask[]>(routes.generateTasks(id))
    },

    /** 获取计划的任务列表 */
    getScheduleTasks(id: number | string, params?: LpaTaskQueryParams) {
      return client.get<PaginationResult<LpaTask>>(routes.schedulesTasks(id), { params })
    },

    /** 月度审核表：按计划+月份+目标区域聚合 检查项×层级×日期 执行矩阵 */
    getMonthlySheet(id: number | string, params: LpaMonthlySheetQueryParams) {
      return client.get<LpaMonthlySheetResponse>(routes.schedulesMonthlySheet(id), { params })
    },

    // ========================================================================
    // 任务管理
    // ========================================================================

    /** 获取任务列表 */
    getTasks(params?: LpaTaskQueryParams) {
      return client.get<PaginationResult<LpaTask>>(routes.lpaTasks(0).replace('/0', ''), { params })
    },

    /** 获取我的任务 */
    getMyTasks(params?: LpaTaskQueryParams) {
      return client.get<PaginationResult<LpaTask>>(routes.lpaTasksMy, { params })
    },

    /** 获取今日任务 */
    getTodayTasks(params?: { layer_code?: string }) {
      return client.get<LpaTask[]>(routes.tasksToday, { params })
    },

    /** 获取任务状态枚举 */
    getTaskStatuses() {
      return client.get<Record<string, string>>(routes.tasksStatuses)
    },

    /** 获取任务来源类型枚举 */
    getTaskSourceTypes() {
      return client.get<Record<string, string>>(routes.tasksSourceTypes)
    },

    /** 创建任务 */
    createTask(data: CreateLpaTaskRequest) {
      return client.post<LpaTask>(routes.lpaTasks(0).replace('/0', ''), data)
    },

    /** 获取任务详情 */
    getTask(id: number | string) {
      return client.get<LpaTask>(routes.lpaTasks(id))
    },

    /** 分配任务 */
    assignTask(id: number | string, data: { assignee_id: number; assignee_name?: string }) {
      return client.post<LpaTask>(routes.tasksAssign(id), data)
    },

    /** 开始审核 */
    startTask(id: number | string) {
      return client.post<LpaTask>(routes.tasksStart(id))
    },

    /** 取消任务 */
    cancelTask(id: number | string) {
      return client.post<LpaTask>(routes.tasksCancel(id))
    },

    /** 完成审核 */
    completeTask(id: number | string) {
      return client.post<LpaTask>(routes.tasksComplete(id))
    },

    /** 获取任务检查项快照列表（包含已提交结果） */
    getTaskItems(id: number | string) {
      return client.get<LpaTaskItem[]>(routes.tasksItems(id))
    },

    /** 提交审核结果 */
    submitResult(taskId: number | string, data: SubmitLpaResultRequest) {
      return client.post<LpaResult>(routes.results(taskId), data)
    },

    /** 批量提交审核结果 */
    batchSubmitResults(taskId: number | string, data: BatchSubmitLpaResultsRequest) {
      return client.post<LpaResult[]>(routes.resultsBatch(taskId), data)
    },

    // ========================================================================
    // 问题项管理
    // ========================================================================

    /** 获取问题项列表 */
    getFindings(params?: LpaFindingQueryParams) {
      return client.get<PaginationResult<LpaFinding>>(routes.lpaFindings(0).replace('/0', ''), { params })
    },

    /** 获取我负责的问题项 */
    getMyFindings(params?: LpaFindingQueryParams) {
      return client.get<PaginationResult<LpaFinding>>(routes.findingsMy, { params })
    },

    /** 获取问题严重程度枚举 */
    /**
     * 导出 LPA 任务列表（与列表同一筛选口径）
     *
     * @returns Blob xlsx 文件流
     */
    exportTasks(params?: LpaTaskQueryParams) {
      return client.get<Blob>(routes.tasksExport, {
        params,
        responseType: 'blob',
      });
    },

    /**
     * 导出 LPA 问题项列表（与列表同一筛选口径）
     *
     * @returns Blob xlsx 文件流
     */
    exportFindings(params?: LpaFindingQueryParams) {
      return client.get<Blob>(routes.findingsExport, {
        params,
        responseType: 'blob',
      });
    },

    /**
     * 更新 LPA 模块运行时开关（Web 管理端入口）
     */
    updateLpaConfig(data: UpdateLpaConfigRequest) {
      return client.put<LpaModuleConfig>(routes.lpaConfigUpdate, data)
    },

    getFindingSeverities() {
      return client.get<Record<string, string>>(routes.findingsSeverities)
    },

    /** 获取问题状态枚举 */
    getFindingStatuses() {
      return client.get<Record<string, string>>(routes.findingsStatuses)
    },

    /** 获取问题项详情 */
    getFinding(id: number | string) {
      return client.get<LpaFinding>(routes.lpaFindings(id))
    },

    /** 更新问题项（处理反馈：原因分析/纠正/预防/整改证据；上报信息走结果修订） */
    updateFinding(id: number | string, data: UpdateLpaFindingRequest) {
      return client.put<LpaFinding>(routes.findings(id), data)
    },

    /** 指派问题责任人（员工档案域，跨仓记录 2026-10-09 责任人档案化） */
    assignFinding(id: number | string, data: AssignLpaFindingRequest) {
      return client.post<LpaFinding>(routes.findingsAssign(id), data)
    },

    /** 一键创建 NCR（来源 LPA分层审核，返回创建的 NCR） */
    createFindingNcr(id: number | string) {
      return client.post<NCROrder>(routes.findingsCreateNcr(id))
    },

    /** 关闭问题项 */
    closeFinding(id: number | string, data?: { corrective_action?: string; preventive_action?: string }) {
      return client.post<LpaFinding>(routes.findingsClose(id), data)
    },

    /** 验证问题项（可选提交验证证据） */
    verifyFinding(id: number | string, data?: VerifyLpaFindingRequest) {
      return client.post<LpaFinding>(routes.verify(id), data)
    },

    /**
     * 作废问题项（无效/重复上报处置，必须给出原因）
     */
    cancelFinding(id: number | string, data: CancelLpaFindingRequest) {
      return client.post<LpaFinding>(routes.findingsCancel(id), data)
    },

    // ========================================================================
    // 统计分析
    // ========================================================================

    /** 获取总览统计 */
    getOverviewStatistics(params?: LpaStatisticsQueryParams) {
      return client.get<LpaOverviewStatistics>(routes.statisticsOverview, { params })
    },

    /** 获取完成率统计 */
    getCompletionStatistics(params: LpaStatisticsQueryParams) {
      return client.get<LpaCompletionStatisticsItem[]>(routes.statisticsCompletion, { params })
    },

    /** 获取问题统计 */
    getFindingStatistics(params?: LpaStatisticsQueryParams) {
      return client.get<LpaFindingStatistics>(routes.statisticsFindings, { params })
    },

    /** 获取趋势分析 */
    getTrendStatistics(params: LpaStatisticsQueryParams) {
      return client.get<LpaTrendStatistics>(routes.statisticsTrend, { params })
    },

    /** 获取层级对比统计 */
    getLayerComparisonStatistics(params?: LpaStatisticsQueryParams) {
      return client.get<LpaLayerComparisonStatistics[]>(routes.statisticsLayerComparison, { params })
    },

    /** 获取8D关联任务统计 */
    getEightDTaskStatistics(eightDId?: number) {
      return client.get<LpaEightDTaskStatistics>(
        routes.statisticsEightDTasks,
        { params: { eight_d_id: eightDId } }
      )
    },

    // ========================================================================
    // 8D-LPA配置
    // ========================================================================

    /** 获取所有8D-LPA配置 */
    getEightDLpaConfigs() {
      return client.get<EightDLpaConfig[]>(routes.lpaEightDConfigs)
    },

    /** 更新8D-LPA配置 */
    updateEightDLpaConfig(severity: string, data: UpdateEightDLpaConfigRequest) {
      return client.put<EightDLpaConfig>(routes.eightDConfigs(severity), data)
    },

    /** 获取阻塞模式枚举 */
    getBlockingModes() {
      return client.get<Record<string, string>>(routes.eightDConfigsBlockingModes)
    },

    /** 检查8D报告的LPA状态 */
    checkEightDLpaStatus(eightDId: number | string) {
      return client.get<EightDLpaStatus>(routes['8dReportsLpaStatus'](eightDId))
    },

    /** 从8D报告触发LPA任务 */
    triggerLpaFromEightD(eightDId: number | string) {
      return client.post<{ created_tasks: number; message: string }>(routes.triggerLpa(eightDId))
    },

    /** 8D报告处理中新增审核项到目标模板（来源强制 EIGHT_D，执行时不允许跳过/不适用） */
    addEightDReportLpaItem(eightDId: number | string, data: AddEightDLpaItemRequest) {
      return client.post<LpaItem>(routes['8dReportsLpaItems'](eightDId), data)
    },

    // ========================================================================
    // 模块开关
    // ========================================================================

    /** LPA 模块开关（复核/交叉抽检是否开启，供前端呈现门控） */
    getLpaConfig() {
      return client.get<LpaModuleConfig>(routes.lpaConfig)
    },

    // ========================================================================
    // 上级复核
    // ========================================================================

    /** 待我复核任务列表 */
    getPendingReviewTasks(params?: LpaTaskQueryParams) {
      return client.get<PaginationResult<LpaTask>>(routes.tasksPendingReview, { params })
    },

    /** 上级复核（单复核人制，先到先得） */
    reviewTask(id: number | string, data: ReviewLpaTaskRequest) {
      return client.post<LpaTask>(routes.tasksReview(id), data)
    },

    // ========================================================================
    // 审核花名册
    // ========================================================================

    /** 花名册列表 */
    getRosters(params?: LpaRosterQueryParams) {
      return client.get<PaginationResult<LpaAuditorRoster>>(routes.lpaRosters, { params })
    },

    /** 获取组织架构候选建议（不落库，仅供人工确认） */
    prefillRosters() {
      return client.get<LpaRosterPrefillCandidate[]>(routes.lpaRostersPrefill)
    },

    /** 新增花名册条目 */
    createRoster(data: CreateLpaRosterRequest) {
      return client.post<LpaAuditorRoster>(routes.lpaRosters, data)
    },

    /** 批量保存花名册（预生成确认后落库，已存在条目自动跳过） */
    batchCreateRosters(data: BatchCreateLpaRostersRequest) {
      return client.post<{ created: number }>(routes.lpaRostersBatch, data)
    },

    /** 更新花名册条目 */
    updateRoster(id: number | string, data: Partial<CreateLpaRosterRequest>) {
      return client.put<LpaAuditorRoster>(routes.lpaRoster(id), data)
    },

    /** 删除花名册条目 */
    deleteRoster(id: number | string) {
      return client.delete<void>(routes.lpaRoster(id))
    },
  }
}
